import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import { cachedGet, queuedMutate } from '@/lib/offlineApi';

// Owns transcript fetching, edit/save, download, summary export, and diarization.
// All UI state for the transcript card lives here.
export function useTranscriptData({ sessionId, token, navigate, user }) {
  const [transcript, setTranscript] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editedText, setEditedText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sessionData, setSessionData] = useState(null);
  const [showDiarized, setShowDiarized] = useState(false);
  const [diarizing, setDiarizing] = useState(false);
  const [sharingSummary, setSharingSummary] = useState(false);

  const fetchTranscript = async () => {
    try {
      const [transcriptRes, sessionRes] = await Promise.all([
        cachedGet(`${API}/transcripts/session/${sessionId}`, token, { ttlMs: 7 * 24 * 60 * 60 * 1000 }),
        cachedGet(`${API}/sessions/${sessionId}`, token, { ttlMs: 7 * 24 * 60 * 60 * 1000 }).catch(() => null),
      ]);
      setTranscript(transcriptRes.data);
      setEditedText(transcriptRes.data.edited_text || transcriptRes.data.text);
      if (sessionRes) setSessionData(sessionRes.data);
      if (transcriptRes.fromCache) {
        toast.info('Viewing cached transcript — fresh data will load when you reconnect.', { duration: 3000 });
      }
      return { transcript: transcriptRes.data, session: sessionRes?.data || null };
    } catch (error) {
      toast.error('Failed to load transcript');
      navigate('/');
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTranscript();
  }, [sessionId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await queuedMutate(
        'PUT',
        `${API}/transcripts/${transcript.id}`,
        { edited_text: editedText },
        token,
        { label: `Save transcript ${transcript.id.slice(0, 8)}` }
      );
      if (result.queued) {
        toast.success('Transcript saved locally — will sync when you reconnect.');
        // Optimistically reflect the edit in local UI state
        setTranscript((prev) => prev ? { ...prev, edited_text: editedText } : prev);
        setEditing(false);
      } else {
        toast.success('Transcript updated successfully');
        setEditing(false);
        fetchTranscript();
      }
    } catch (error) {
      toast.error('Failed to update transcript');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async (format) => {
    try {
      const response = await axios.get(
        `${API}/transcripts/${transcript.id}/export/${format}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `transcript_${transcript.id}.${format === 'pdf' ? 'pdf' : 'txt'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Transcript downloaded as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Failed to download transcript');
    }
  };

  const handleShareSummary = async (opts = { openMail: true }) => {
    if (sharingSummary) return;
    setSharingSummary(true);
    try {
      const response = await axios.get(
        `${API}/sessions/${sessionId}/export/summary-pdf`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      );

      let filename = `session_summary_${sessionId.slice(0, 8)}.pdf`;
      const cd = response.headers?.['content-disposition'] || response.headers?.get?.('content-disposition');
      const match = cd && /filename="?([^";]+)"?/.exec(cd);
      if (match) filename = match[1];

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);

      toast.success('Session summary PDF downloaded — attach it to your email');

      if (opts.openMail) {
        const patientName = sessionData?.patient_name || transcript?.patient_name || 'Patient';
        const dateStr = (sessionData?.session_date || sessionData?.created_at || '').slice(0, 10);
        const subject = encodeURIComponent(`Session summary — ${patientName}${dateStr ? ` — ${dateStr}` : ''}`);
        const body = encodeURIComponent(
          `Hi,\n\nPlease find attached the session summary for ${patientName}${dateStr ? ` (${dateStr})` : ''}.\n` +
          `It includes the diagnostic evaluation, medical SOAP note, psychotherapy note, ICD-10/CPT codes, and the session transcript.\n\n` +
          `Attach the downloaded PDF named "${filename}" before sending.\n\nBest,\n${user?.name || user?.email || ''}\n`
        );
        window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
      }
    } catch (error) {
      const status = error?.response?.status;
      if (status === 404) toast.error('Session data not found — generate notes first.');
      else toast.error('Failed to build session summary');
    } finally {
      setSharingSummary(false);
    }
  };

  const handleDiarize = async () => {
    if (!transcript) return;
    setDiarizing(true);
    try {
      const response = await axios.post(
        `${API}/transcripts/${transcript.id}/diarize`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTranscript((prev) => ({
        ...prev,
        diarized_text: response.data.diarized_text,
        diarization_status: 'completed',
      }));
      setShowDiarized(true);
      toast.success('Speaker diarization complete');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Diarization failed');
    } finally {
      setDiarizing(false);
    }
  };

  const cancelEdit = () => {
    setEditing(false);
    setEditedText(transcript.edited_text || transcript.text);
  };

  return {
    transcript, setTranscript,
    editing, setEditing,
    editedText, setEditedText,
    loading,
    saving,
    sessionData, setSessionData,
    showDiarized, setShowDiarized,
    diarizing,
    sharingSummary,
    fetchTranscript,
    handleSave,
    handleDownload,
    handleShareSummary,
    handleDiarize,
    cancelEdit,
  };
}
